import { defaultTemplateId } from './../utils/constants'
import { navigate } from 'hookrouter'
import objectToFormData from 'object-to-formdata'
import snakecaseKeys from 'snakecase-keys'

import {
  AcceptedInviteUser,
  ActionableDispatch,
  Company,
  Field,
  LoginError,
  Model,
  NewCompanyFormInputProps,
  Project,
  SignupError,
  TemplateDocument,
  User,
} from 'types'
import {
  addError,
  addNotice,
  persistCompanies,
  persistDocuments,
  persistDirectories,
  persistProducts,
  persistProjects,
  persistTemplateDocuments,
  persistUser,
  persistOtherRoles,
  removeErrors,
  removeTemplateDocuments,
  removeUser,
  addPermissions,
  updatePermissions,
  addDirectories,
} from './actions'
import { APP_ROUTES } from 'pages/AppRouter'
import { LOGIN_ROUTES } from 'pages/LoginRouter'
import * as PDSApi from 'lib/PDSApi'
import { persistUserBrowser, removeUserBrowser } from 'lib/BrowserStorage'
import { toast } from 'react-toastify'

const showSuccessMessage = (text: any) => {
  toast(text, {
    position: 'top-right',
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  })
}

const showErrorMessage = (text: any) => {
  toast.error(text, {
    position: 'top-right',
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  })
}

export enum FormKey {
  document = 'document',
  templateDocument = 'template_document',
  templateDirectory = 'template_directory',
  user = 'user',
}

export const toFormData = (inputs: any, formKey: FormKey) => {
  const keys = snakecaseKeys(inputs, { exclude: ['_destroy'] })
  const formData = objectToFormData(keys)

  return formData
}

export const formatRegistrationErrors = (error: SignupError): string[] => {
  const details = { ...error.response.data.errors[0].detail }
  const inputsWithErrors: string[] = Object.keys(details)
  const replaceUnderscoreWithSpace = (input: string) => input.replace(/_/, ' ')
  const formattedErrors: string[] = []
  inputsWithErrors.forEach(input =>
    formattedErrors.push(
      `${replaceUnderscoreWithSpace(input)} ${details[input][0]}`,
    ),
  )
  return formattedErrors
}

export const extractLoginError = (error: LoginError): string =>
  error.response.data

export const SUCCESSFUL_INVITE = 'Success! Please log in.'

export const acceptInvite = async (
  dispatch: ActionableDispatch,
  token: string,
  userFormDetails: AcceptedInviteUser,
) => {
  try {
    await PDSApi.patchAcceptInvite(token, userFormDetails)

    dispatch(addNotice(SUCCESSFUL_INVITE))
    navigate(LOGIN_ROUTES.login, true)
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

export const updateCompany = async (
  dispatch: ActionableDispatch,
  companyAttributes: Pick<Company, 'name'>,
  companyId: number,
) => {
  try {
    const updatedCompany = await PDSApi.patchCompany(
      companyAttributes,
      companyId,
    )

    dispatch(persistCompanies([updatedCompany]))
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

const DOCUMENT_UPDATED_TEXT = 'Template updated.'
export const updateTemplateDocument = async (
  dispatch: ActionableDispatch,
  templateDocumentId: number,
  inputs: Pick<TemplateDocument, 'title'> & TemplateDocParams,
) => {
  dispatch(removeErrors())

  const newInputs = {
    ...inputs,
    fieldsAttributes: inputs.fieldsAttributes.map(fieldAttribute => ({
      ...fieldAttribute,
      userId:
        fieldAttribute.userId === undefined ? null : fieldAttribute.userId,
    })),
  }

  try {
    const formData = toFormData(
      { templateDocument: newInputs },
      FormKey.templateDocument,
    )
    if (newInputs.file) {
      formData.append('template_document[file]', newInputs.file)
    }
    const template = await PDSApi.patchTemplateDocument(
      formData,
      templateDocumentId,
    )

    dispatch(persistTemplateDocuments([template]))
    dispatch(addNotice(DOCUMENT_UPDATED_TEXT))
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

export const addProjectCoordinatorToProject = async (
  dispatch: ActionableDispatch,
  projectId: number,
  userEmail: string,
) => {
  try {
    const project = await PDSApi.postProjectCoordinatorsRole(
      projectId,
      userEmail,
    )

    dispatch(persistProjects([project]))
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

export const addSignatoryToProject = async (
  dispatch: ActionableDispatch,
  projectId: number,
  userEmail: string,
) => {
  try {
    const project = await PDSApi.postSignatoriesRole(projectId, userEmail)

    dispatch(persistProjects([project]))
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

export const createProject = async (
  dispatch: ActionableDispatch,
  companyId: number,
  inputs: Pick<Project, 'name'>,
) => {
  dispatch(removeErrors())
  try {
    const project = await PDSApi.postProject(companyId, inputs)

    dispatch(persistProjects([project]))
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

interface TemplateDocParams {
  file: File
  fieldsAttributes: Field[]
}

export const DOCUMENT_CREATED_TEXT = 'Document Created!'
export const createTemplateDocument = async (
  dispatch: ActionableDispatch,
  projectId: number,
  inputs: Pick<TemplateDocument, 'title'> & TemplateDocParams,
) => {
  dispatch(removeErrors())
  try {
    const formData = toFormData(
      { templateDocument: inputs },
      FormKey.templateDocument,
    )
    if (inputs.file) {
      formData.append('template_document[file]', inputs.file)
    }
    const template = await PDSApi.postTemplateDocument(projectId, formData)
    showSuccessMessage('Template Document Created Successfully')
    dispatch(persistTemplateDocuments([template]))
    dispatch(addNotice(DOCUMENT_CREATED_TEXT))
  } catch (error) {
    error = JSON.stringify(error)
    if (error.includes('403'))
      showErrorMessage('You are not Allowed for this action')
    else showErrorMessage('Internal Server Error')
    dispatch(addError(error.toString()))
  }
}

export const FOLDER_CREATED_TEXT = 'Folder Created!'
export const createTemplateDirectory = async (
  dispatch: ActionableDispatch,
  projectId: number,
  inputs: Pick<TemplateDocument, 'name'>,
) => {
  dispatch(removeErrors())
  try {
    const formData = toFormData(
      { templateDirectory: inputs },
      FormKey.templateDirectory,
    )
    const template = await PDSApi.postTemplateDirectory(projectId, formData)

    dispatch(persistTemplateDocuments([template]))
    dispatch(addNotice(FOLDER_CREATED_TEXT))
    showSuccessMessage('Template Directory Created Successfully.')
  } catch (error) {
    error = JSON.stringify(error)
    if (error.includes('403'))
      showErrorMessage('You are not Allowed for this action')
    else showErrorMessage('Internal Server Error')
    dispatch(addError(error.toString()))
  }
}

export const duplicateTemplate = async (
  dispatch: ActionableDispatch,
  newProjectId: number,
  templateId: number,
) => {
  try {
    await PDSApi.duplicateTemplateDocument(newProjectId, templateId)
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

export const duplicateTemplateToFolder = async (
  dispatch: ActionableDispatch,
  newFolderId: number,
  templateId: number,
) => {
  try {
    // const params = { template_directory_id: newFolderId }
    const template = await PDSApi.duplicateTemplateFolder(
      newFolderId,
      // params,
      templateId,
    )
    dispatch(persistTemplateDocuments([template]))
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

export const viewTemplateDirectories = async (
  dispatch: ActionableDispatch,
  projectId: number | undefined,
) => {
  try {
    const directories = await PDSApi.viewTemplateDirectories(projectId)
    console.log(directories, 'directoriesdirectories')
    dispatch(addDirectories(directories))
  } catch (error) {
    dispatch(addError(error.toString()))
    // showErrorMessage('Internal Server Error');
  }
}

export const moveTemplate = async (
  dispatch: ActionableDispatch,
  template_directory_id: number,
  templateId: number,
) => {
  try {
    const template = await PDSApi.moveTemplateDocument(
      template_directory_id,
      templateId,
    )
    showSuccessMessage('Template Moved to Another Folder Successfully.')
    dispatch(removeTemplateDocuments([template]))
  } catch (error) {
    dispatch(addError(error.toString()))
    showErrorMessage('Internal Server Error')
  }
}

export const deleteTemplate = async (
  dispatch: ActionableDispatch,
  templateId: number,
) => {
  try {
    const template = await PDSApi.deleteTemplateDocument(templateId)
    dispatch(removeTemplateDocuments([template]))
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

export const deleteDirectory = async (
  dispatch: ActionableDispatch,
  directoryId: number | undefined,
) => {
  try {
    await PDSApi.deleteDirecory(directoryId)
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

export const deleteRole = async (
  dispatch: ActionableDispatch,
  roleType: string,
  roleId: any,
  projectId?: number | undefined,
  userRoleType?: number | undefined,
  comapnyId?: number | undefined,
  templateDocumentId?: number | undefined,
) => {
  try {
    if (userRoleType == 1)
      await PDSApi.deleteCompanyMangerRoles(comapnyId, roleId)
    else await PDSApi.deleteRoles(roleType, roleId, projectId)
    if (userRoleType == 1) fetchCompanies(dispatch)
    else getSpecificRoles(dispatch, projectId, templateDocumentId, roleType)
    showSuccessMessage('User Role Updated Successfullly.')
  } catch (error) {
    error = JSON.stringify(error)
    if (error.includes('403'))
      showErrorMessage('You are not Allowed for this action')
    else showErrorMessage('Internal Server Error')
    dispatch(addError(error.toString()))
  }
}

export const createCompany = async (
  dispatch: ActionableDispatch,
  inputs: NewCompanyFormInputProps,
) => {
  const postCompanyParams = {
    company: { name: inputs.name },
    payment: {
      payment_method_id: inputs.paymentMethodId,
      price_id: inputs.product,
    },
  }

  dispatch(removeErrors())
  try {
    const company = await PDSApi.postCompany(postCompanyParams)

    dispatch(persistCompanies([company]))
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

export const fetchDocuments = async (
  dispatch: ActionableDispatch,
  templateId: number,
  page = 1,
) => {
  try {
    const documents = await PDSApi.getDocuments(templateId, page)
    dispatch(persistDocuments(documents))
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

export const fetchTemplateDocuments = async (
  dispatch: ActionableDispatch,
  projectId: number,
  template_directory_id?: number,
  page = 1,
  localTemplates: any = [],
) => {
  try {
    const templates = await PDSApi.getTemplateDocuments(
      projectId,
      template_directory_id,
      page,
    )
    let allTemplates = [...localTemplates, ...templates]
    templates.forEach(async template => {
      !template.isDirectory
        ? fetchDocuments(dispatch, template.id)
        : fetchTemplateDirectories(dispatch, projectId, template.id)
    })
    dispatch(persistTemplateDocuments(allTemplates))
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

export const fetchTemplateDirectories = async (
  dispatch: ActionableDispatch,
  projectId: number,
  templateId: number,
  page = 1,
) => {
  try {
    const documents = await PDSApi.getTemplateDirectories(
      projectId,
      templateId,
      page,
    )
    dispatch(persistDirectories(documents))
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

export const fetchTemplateDocument = async (
  dispatch: ActionableDispatch,
  templateDocumentId: number,
) => {
  try {
    const template = await PDSApi.getTemplateDocument(templateDocumentId)
    dispatch(persistTemplateDocuments([template]))
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

export const fetchProjects = async (
  dispatch: ActionableDispatch,
  companyId: number,
) => {
  try {
    const projects = await PDSApi.getProjects(companyId)

    dispatch(persistProjects(projects))
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

export const fetchCompanies = async (dispatch: ActionableDispatch) => {
  try {
    const companies = await PDSApi.getCompanies()

    dispatch(persistCompanies(companies))
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

export const fetchUser = async (
  dispatch: ActionableDispatch,
  userId: number,
) => {
  dispatch(removeErrors())
  try {
    const user = await PDSApi.getUser(userId)

    if (user) {
      dispatch(persistUser(user))
    } else {
      dispatch(removeUser())
      navigate('/')
    }
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

export const triggerPasswordReset = async (
  dispatch: ActionableDispatch,
  email: string,
  successfulCallback: () => void,
) => {
  try {
    await PDSApi.postResetPassword(email)
    successfulCallback()
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

export const updateProjectStatus = async (
  dispatch: ActionableDispatch,
  projectId: number | undefined,
  payload: any,
) => {
  try {
    await PDSApi.updateProjectStatus(projectId, payload)
    showSuccessMessage('Project Status Updated Successfully.')
  } catch (error) {
    dispatch(addError(error.toString()))
    showSuccessMessage('Internal Server Error.')
  }
}

export const mismatchPasswordError =
  'Password and Password Confirmation need to match'
export const resetPassword = async (
  dispatch: ActionableDispatch,
  password: string,
  passwordConfirmation: string,
  token: string,
  successfulCallback: () => void,
) => {
  try {
    if (password !== passwordConfirmation) {
      throw new Error(mismatchPasswordError)
    }

    await PDSApi.patchResetPassword(password, passwordConfirmation, token)
    successfulCallback()
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

export const logIn = async (
  dispatch: ActionableDispatch,
  email: string,
  password: string,
) => {
  dispatch(removeErrors())
  try {
    const userWithAuthToken = await PDSApi.postLogin(email, password)
    const { authToken, ...user } = userWithAuthToken
    if (user.id) {
      persistUserBrowser(user.id, authToken)
    }

    dispatch(persistUser(user))
    navigate(APP_ROUTES.root, true)
  } catch (error) {
    dispatch(addError(extractLoginError(error)))
  }
}

export const logOut = async (dispatch: ActionableDispatch) => {
  removeUserBrowser()
  navigate(LOGIN_ROUTES.login, true)

  dispatch(removeUser())
}

export const updateUser = async (
  dispatch: ActionableDispatch,
  userAttributes: Exclude<User, Model>,
  userId: number,
) => {
  try {
    const updatedUser = await PDSApi.patchUser(userAttributes, userId)

    dispatch(persistUser(updatedUser))
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

type UserRegistrationParams = {
  firstName: string
  lastName: string
  email: string
  password: string
  passwordConfirmation: string
}

export const registerUser = async (
  dispatch: ActionableDispatch,
  inputs: UserRegistrationParams,
) => {
  dispatch(removeErrors())
  try {
    const formData = toFormData({ user: inputs }, FormKey.user)
    const registeredUser = await PDSApi.postSignup(formData)

    const { authToken, ...user } = registeredUser
    if (user.id) {
      persistUserBrowser(user.id, authToken)
    }
    dispatch(persistUser(user))
    navigate(APP_ROUTES.root, false)
  } catch (error) {
    formatRegistrationErrors(error).map(formattedError =>
      dispatch(addError(formattedError)),
    )
  }
}

export const addCompanyManagerToCompany = async (
  dispatch: ActionableDispatch,
  companyId: number | undefined,
  userEmail: string,
) => {
  try {
    const company = await PDSApi.postCompanyManagerRole(companyId, userEmail)
    showSuccessMessage('Added Successfully')
    dispatch(persistCompanies([company]))
  } catch (error) {
    error = JSON.stringify(error)
    if (error.includes('403'))
      showErrorMessage('You are not Allowed for this action')
    else showErrorMessage('Internal Server Error')
    dispatch(addError(error.toString()))
  }
}

export const addOtherRoles = async (
  dispatch: ActionableDispatch,
  projectId: number | undefined,
  templateDocumentId: number | undefined,
  selectedFolder: number[] | undefined,
  userEmail: string,
  type: string | undefined,
) => {
  try {
    let templateDocumentIds = selectedFolder?.join(',')

    const role = await PDSApi.postOtherRoles(
      projectId,
      templateDocumentIds,
      userEmail,
      type,
    )
    let {
      project_managers,
      signatories,
      sub_contractors,
      super_foremans,
      project_coordinators,
      folder_access = [],
    } = role.data
    dispatch(
      persistOtherRoles({
        project_managers,
        signatories,
        sub_contractors,
        super_foremans,
        project_coordinators,
        folder_access,
      }),
    )
    showSuccessMessage('Added Successfully')
  } catch (error) {
    error = JSON.stringify(error)
    if (error.includes('403'))
      showErrorMessage('You are not Allowed for this action')
    else showErrorMessage('Internal Server Error')
    dispatch(addError(error.toString()))
  }
}

export const getOtherRoles = async (
  dispatch: ActionableDispatch,
  projectId: number | undefined,
  templateDocumentId: number | undefined,
  userType: string | undefined,
) => {
  try {
    let templateDocumentIds = templateDocumentId || defaultTemplateId
    const role = await PDSApi.getOtherRoles(
      projectId,
      templateDocumentIds,
      userType,
    )
    let {
      project_managers,
      signatories,
      sub_contractors,
      super_foremans,
      project_coordinators,
    } = role.data
    dispatch(
      persistOtherRoles({
        project_managers,
        signatories,
        sub_contractors,
        super_foremans,
        project_coordinators,
      }),
    )
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}
export const getSpecificRoles = async (
  dispatch: ActionableDispatch,
  projectId: number | undefined,
  templateDocumentId: number | undefined,
  userType: string | undefined,
) => {
  try {
    let templateDocumentIds = templateDocumentId || defaultTemplateId
    const roles = await PDSApi.getOtherRoles(
      projectId,
      templateDocumentIds,
      userType,
    )
    let project_managers
    let super_foremans
    let sub_contractors
    let project_coordinators
    let folder_access

    let rolesData = []
    if (userType == 'project_coordinator_roles') {
      rolesData.push(roles.data)
    } else {
      rolesData = roles.data
    }

    folder_access = rolesData?.[0]?.folder_access || []

    if (userType == 'project_manager_roles')
      project_managers = roles.data?.[0]?.project_managers || []
    else if (userType == 'super_foreman_roles')
      super_foremans = roles.data?.[0]?.super_foremans || []
    else if (userType == 'sub_contractor_roles')
      sub_contractors = roles.data?.[0]?.sub_contractors || []
    else if (userType == 'project_coordinator_roles')
      project_coordinators = rolesData?.[0]?.project_coordinators || []

    dispatch(
      persistOtherRoles({
        project_managers: project_managers,
        sub_contractors: sub_contractors,
        super_foremans: super_foremans,
        project_coordinators: project_coordinators,
        folder_access,
      }),
    )
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}
export const getPermissionsList = async (
  dispatch: ActionableDispatch,
  comapnyId: number | undefined,
  roleType: number | undefined,
) => {
  try {
    let userType
    if (roleType == 1) userType = 'CompanyManagerRole'
    else if (roleType == 2) userType = 'ProjectManagerRole'
    else if (roleType == 3) userType = 'SuperForemanRole'
    else if (roleType == 4) userType = 'SubContractorRole'
    else if (roleType == 5) userType = 'ProjectCoordinatorRole'
    const permission = await PDSApi.getPermissions(comapnyId, userType)
    dispatch(addPermissions(permission.data))
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

export const updatePermissionsList = async (
  dispatch: ActionableDispatch,
  comapnyId: number | undefined,
  roleId: number | undefined,
  payload: any,
  data: [],
) => {
  try {
    await PDSApi.updatePermissions(comapnyId, roleId, payload)
    dispatch(updatePermissions([data]))
    showSuccessMessage('Permission Updated Successfully')
  } catch (error) {
    error = JSON.stringify(error)
    if (error.includes('403'))
      showErrorMessage('You are not Allowed for this action')
    else showErrorMessage('Internal Server Error')
    // dispatch(addError(error.toString()))
  }
}

export const fetchStripeProducts = async (dispatch: ActionableDispatch) => {
  try {
    const products = await PDSApi.getStripeProducts()

    dispatch(persistProducts(products))
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}

export const reinviteUser = async (
  dispatch: ActionableDispatch,
  userId: number,
) => {
  try {
    await PDSApi.postReinvite(userId)
    showSuccessMessage('Invitation Sent Successfully')
    dispatch(addNotice('User was successfully re-invited'))
  } catch (error) {
    error = JSON.stringify(error)
    if (error.includes('403'))
      showErrorMessage('You are not Allowed for this action')
    else showErrorMessage('Internal Server Error')
    dispatch(addError(error.toString()))
  }
}

export const upgradeCompanyPlan = async (
  dispatch: ActionableDispatch,
  company: Company,
  newPlanId: string,
) => {
  try {
    const { stripeCustomerId } = company

    await PDSApi.postUpgrade(stripeCustomerId, newPlanId)

    dispatch(addNotice('Plan was successfully upgraded'))
  } catch (error) {
    dispatch(addError(error.toString()))
  }
}
